import { Injectable } from "@angular/core";
import { deepCopy } from "@app/class/Utilities";
import { SortDirection } from "@app/enum/SortDirection";
import { IFilterOptions } from "@app/interfaces/filters/IFilterOptions";
import { ColumnDataType } from "@app/main/components/reports/enums/ColumnDataType";
import { IDataDefinition } from "@app/main/components/reports/models/IReportDefinition";
import { ReportParameterType } from "@app/main/components/reports/models/IReportParameters";
import { IReportRow } from "@app/main/components/reports/models/IReportRow";
import { DateService } from "@app/services/date.service";
import { FilterOptionsService } from "@app/services/filterOptions.service";
import { IDashboardWidget } from "./_models/IDashboardWidget";
import { IPooledRequest } from "./_models/IPooledRequest";
import { DashboardService } from "./dashboard.service";

@Injectable({
    providedIn: 'root'
})
export class DashboardPoolingService {
    constructor(
        private readonly dashboardService: DashboardService,
        private readonly filterOptionsService: FilterOptionsService,
        private readonly dateService: DateService
    ) { }

    poolRequests(
        widgets: IDashboardWidget[], 
        filterOptions: IFilterOptions, 
        loadingUpdateCallback?: (pooledRequest: IPooledRequest) => void
    ): Promise<void> {
        const compatibleWidgetLists = this.createCompatibleWidgetLists(widgets);
    
        const pooledRequests: IPooledRequest[] = [];
        const pooledRequestPromises: Promise<void>[] = [];
    
        compatibleWidgetLists.forEach(compatibleWidgets => {
            const pooledRequest = this.createPooledRequest(compatibleWidgets, filterOptions);
    
            pooledRequest.widgets.forEach(w => { w.loading = true; });
            loadingUpdateCallback?.(pooledRequest);
    
            pooledRequest.promise.finally(() => {
                pooledRequest.completed = true;
    
                pooledRequest.widgets.forEach(w => { w.loading = false; });               
                loadingUpdateCallback?.(pooledRequest);
            });
    
            pooledRequests.push(pooledRequest);
            pooledRequestPromises.push(pooledRequest.promise);
        });
    
        return Promise.all(pooledRequestPromises).then(() => {});
    }

    private createCompatibleWidgetLists(widgets: IDashboardWidget[]): IDashboardWidget[][] {
        const compatibleWidgetLists: IDashboardWidget[][] = [];
    
        widgets.forEach(widget => {
            this.dashboardService.dataSourceToWidgetDataDefinition(widget);
    
            // Find the first compatible list or create a new one if not found
            const compatibleList = compatibleWidgetLists.find(compatibleWidgetList =>
                compatibleWidgetList.every(compatibleWidget =>
                    this.definitionsAreCompatible(widget, compatibleWidget)
                )
            );
    
            if (compatibleList) {
                compatibleList.push(widget); // Add to existing compatible list
            } else {
                compatibleWidgetLists.push([widget]); // Create a new compatible list
            }
        });
    
        return compatibleWidgetLists;
    }

    private definitionsAreCompatible(newWidget: IDashboardWidget, existingWidget: IDashboardWidget): boolean {
        const newDefinition = newWidget.dataDefinition;
        const existingDefinition = existingWidget.dataDefinition;
    
        return (
            newDefinition.RowDataSource === existingDefinition.RowDataSource &&
            newDefinition.SubRowDataSource === existingDefinition.SubRowDataSource
            && JSON.stringify(newDefinition.Parameters) === JSON.stringify(existingDefinition.Parameters)
        );
    }
    
    private createPooledRequest(compatibleWidgets: IDashboardWidget[], filterOptions: IFilterOptions): IPooledRequest {
        const pooledRequest: IPooledRequest = {
            id: this.getRandomNumber(0, 1000),
            widgets: compatibleWidgets,
            combinedDefinition: this.combineDefinitions(compatibleWidgets)
        };
    
        // Set parameters
        pooledRequest.combinedDefinition.Parameters = this.filterOptionsService.createReportParameters(filterOptions);
    
        // Add custom parameters directly to the Parameters object
        pooledRequest.combinedDefinition.Parameters.CustomParameters = [
            {
                ParameterType: ReportParameterType.InclusiveGroupFiltering,
                Active: true,
                Value: true
            },
            {
                ParameterType: ReportParameterType.ExcludeGpsJourneys,
                Active: true,
                Value: true
            },
            ...compatibleWidgets.flatMap(widget => widget.DataWidgetParameters?.CustomParameters || [])
        ];

        // Populate the pooled request promise
        pooledRequest.promise = this.dashboardService.populateDataDefinition(pooledRequest.combinedDefinition)
            .then(dataDefinition => {
                pooledRequest.responseDataDefinition = dataDefinition;
                this.splitDefinitions(pooledRequest);
            })
            .catch(error => {
                    pooledRequest.failed = true;
            });
    
        return pooledRequest;
    }

    private getRandomNumber(from: number, to: number): number {
        return Math.floor(Math.random() * (to - from + 1) + from);
    }

    private combineDefinitions(compatibleWidgets: IDashboardWidget[]): IDataDefinition {
        const definition = { ...deepCopy(compatibleWidgets[0].dataDefinition) } as IDataDefinition;
    
        Object.assign(definition.TableDefinition, {
            Rows: [],
            TotalsRow: null,
            SummarySections: []
        });
    
        definition.TableDefinition.Headers = compatibleWidgets.slice(1).reduce((headers, widget) => {
            return [...headers, ...deepCopy(widget.dataDefinition.TableDefinition.Headers)];
        }, [...definition.TableDefinition.Headers]);
    
        return definition;
    }

    private splitDefinitions(pooledRequest: IPooledRequest): void {
        let currentHeaderIndex = 1;
    
        pooledRequest.widgets.forEach(widget => {
            const { TableDefinition } = widget.dataDefinition;
            const { Headers, Rows, TotalsRow } = pooledRequest.responseDataDefinition.TableDefinition;
            const headerLengthEnd = currentHeaderIndex + widget.dataDefinition.TableDefinition.Headers.length;
    
            TableDefinition.Headers.unshift(Headers[0]);
    
            TableDefinition.Rows = deepCopy(Rows);
            TableDefinition.TotalsRow = deepCopy(TotalsRow);
    
            TableDefinition.Rows.forEach(row => {
                row.Cells = [row.Cells[0], ...row.Cells.slice(currentHeaderIndex, headerLengthEnd)];
            });
    
            this.sortDefinitionRows(widget);
    
            if (TableDefinition.TotalsRow) {
                TableDefinition.TotalsRow.Cells = [...TableDefinition.TotalsRow.Cells.slice(currentHeaderIndex, headerLengthEnd)];
            }
    
            currentHeaderIndex = headerLengthEnd;
        });
    }

    private sortDefinitionRows(widget: IDashboardWidget): void {
        const { DataWidgetParameters: params, dataDefinition } = widget;
        const { Rows, Headers } = dataDefinition.TableDefinition;
        const sortDirection = params.DataSourceSortDirection || SortDirection.Ascending;
        let sortHeaderIndex = params.DataSourceSortIndex ?? 0;
        const sortHeader = Headers[sortHeaderIndex];
        const sortType = sortHeader?.ColumnDefinition?.Type || ColumnDataType.String;
    
        // Early exit if no sorting type is found
        if (!sortHeader) {
            console.warn("Invalid sort header.");
            return;
        }
    
        // Handle sorting by data type
        switch (sortType) {
            case ColumnDataType.Double:
            case ColumnDataType.Int:
            case ColumnDataType.Score:
                this.sortDefinitionRowsAsNumber(Rows, sortHeaderIndex, sortDirection);
                break;
    
            case ColumnDataType.Date:
                this.sortDefinitionRowsAsDate(Rows, sortHeaderIndex, sortDirection);
                break;

            case ColumnDataType.String:
                this.sortDefinitionRowsAsString(Rows, sortHeaderIndex, sortDirection);
                break;
    
            default:
                console.warn("Unable to sort by unsupported type:", ColumnDataType[sortType]);
                break;
        }
    }

    private sortDefinitionRowsAsString(rows: IReportRow[], sortIndex: number, sortDirection: SortDirection) {
        rows.sort((r1, r2) => {
            return sortDirection === SortDirection.Ascending
                ? r1.Cells[sortIndex].Data.localeCompare(r2.Cells[sortIndex].Data)
                : r2.Cells[sortIndex].Data.localeCompare(r1.Cells[sortIndex].Data);
        });
    }

    private sortDefinitionRowsAsDate(rows: IReportRow[], sortIndex: number, sortDirection: SortDirection) {
        rows.sort((r1, r2) => {
            return this.dateService.sort(r1.Cells[sortIndex].Data, r2.Cells[sortIndex].Data, sortDirection);
        });
    }

    private sortDefinitionRowsAsNumber(rows: IReportRow[], sortIndex: number, sortDirection: SortDirection) {
        rows.sort((r1, r2) => {
            var r1num = parseFloat(r1.Cells[sortIndex].Data);
            var r2num = parseFloat(r2.Cells[sortIndex].Data);

            return sortDirection == SortDirection.Ascending
                ? r1num - r2num
                : r2num - r1num;
        });
    }
}
